/* eslint-disable no-unused-vars */
import WarningTopUp from '@/components/Modal/WarningTopUp';
import Navigation from '@/components/Navigation/Navigation';
import ButtonBackHeader from '@/components/ui/Button/ButtonBackHeader';
import BaseInputFormatNumber from '@/components/ui/Input/BaseInputNumberFormat';
import UserHeader from '@/components/User/UserHeader';
import { nfComma } from '@/constants/nf';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import * as yup from 'yup';
import { useDropzone } from 'react-dropzone';
import parse from 'html-react-parser';
import { CopyAll, Paid } from '@mui/icons-material';
import copyToClipboard from '@/helper/copyToClipboard';
import usePatch from '@/hooks/request/usePatch';
import { toast } from 'react-toastify';
import { route } from '@/constants/routeName';
import CircleLoader from '@/components/ui/Loader/CircleLoader';

const schema = yup.object().shape({
	// nominal: yup.string().required('Nominal is a required field').min(200000, 'Minimum nominal is 200,000'),
	nominal: yup
		.number()
		.typeError('Nominal must be a number')
		.required('Nominal is a required field')
		.min(200000, 'Minimum nominal is 200,000'),
});

const nominalModifier = [200000, 300000, 500000, 1000000];

export default function TopUp() {
	const navigate = useNavigate();
	const innerHeight = useSelector((state) => state.content.innerHeight);
	const customer = useSelector((state) => state.auth.user?.customer);
	const outlet = useSelector((state) => state.content.outlet);
	const paymentTransfer = useSelector((state) => state.content.paymentTransfer);
	const [nominal, setNominal] = useState(null);
	const [step, setStep] = useState(0);
	const [isWarning, setWarning] = useState(false);
	const [file, setFile] = useState(null);

	const {
		handleSubmit,
		setValue,
		trigger,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			nominal: 0,
			file: null,
		},
	});

	const { mutate: createOrderTopUp, isPending } = usePatch({
		url: `point-of-sale/topup_requests`,
		method: 'post',
		type: 'form-data',
		callback: (res) => {
			if (res.status === 201) {
				toast.success(`order created successfully`);
				navigate(`/${route.accountTopUps}/`);
				return;
			}
		},
		showError: true,
	});
	const onSubmit = async (data) => {
		if (!customer) {
			toast.error('Customer not found');
			return;
		}
		if (!data.file || !paymentTransfer || !customer.outlet) {
			return;
		}

		const fData = new FormData();
		fData.append('amount', data.nominal);
		fData.append('note', '');
		fData.append('customer', customer.id);
		fData.append('outlet', customer.outlet);
		fData.append('payment_type', paymentTransfer);
		fData.append('receipt', data.file);
		await createOrderTopUp(fData);
	};

	const { getRootProps, getInputProps } = useDropzone({
		accept: {
			'image/*': '*',
		},
		multiple: false,
		onDrop: (file) => {
			setFile(
				Object.assign(file[0], {
					preview: URL.createObjectURL(file[0]),
				})
			);
			setValue('nominal', +nominal);
			setValue('file', file[0]);
			setTimeout(() => {
				handleSubmit(onSubmit)();
			}, 0);
		},
	});

	const onNominalChange = (e) => {
		const val = e.target.value;
		if (val && +val > 0) {
			setValue('nominal', +val);
			trigger('nominal');
			setNominal(val);
		}
	};

	const { mutate: generateUniqueAmount, isPendingGUniqueAmount } = usePatch({
		url: `point-of-sale/topup_requests/generate_unique_amount`,
		method: 'post',
		callback: (res) => {
			if (res.data.unique_amount) {
				setNominal(res.data.unique_amount);
				setValue('nominal', res.data.unique_amount);
				setStep(1);
			}
		},
		onError: () => {
			toast.error('There is an Error when generating top-up amount');
		},
		showError: true,
	});

	const onPay = async () => {
		if (!nominal || +nominal < 200000) {
			return;
		}
		await generateUniqueAmount({ amount: nominal });
	};

	return (
		<div style={{ minHeight: innerHeight }} className='app-container'>
			<div className='flex flex-col w-full h-full gap-4 p-4 pb-24'>
				<UserHeader />
				<div className='flex flex-col flex-1 w-full h-full gap-12'>
					<div className='relative'>
						<ButtonBackHeader
							className='top-0'
							beforeNavigate={() => {
								if (step > 0) {
									return setWarning(true);
								}
								navigate(-1);
							}}
						/>
						<div className='relative flex items-center justify-center'>
							<span className='ml-4 font-bold'>Top-Up</span>
						</div>
					</div>
					<form onSubmit={handleSubmit(onSubmit)} className='relative flex flex-col flex-1 h-full gap-4 pb-4'>
						{step === 0 && (
							<>
								<div className='flex flex-col gap-2'>
									<span>Enter the top up amount</span>
									<TextField
										name='nominal'
										label=''
										className='w-full'
										size='small'
										value={nominal || 0}
										inputProps={{
											inputMode: 'numeric',
											pattern: '[0-9]*',
											step: '1',
											style: {
												textAlign: 'right',
												// outline: errors?.nominal ? 'none' : 0,
												border: errors?.nominal ? '1.5px solid red' : '1px solid black',
												fontSize: '20px',
											},
										}}
										onChange={onNominalChange}
										InputProps={{
											inputComponent: BaseInputFormatNumber,
										}}
										error={!!errors.nominal}
										helperText={errors.nominal?.message}
										sx={{
											'& .MuiOutlinedInput-root': {
												'& fieldset': {
													borderColor: errors.nominal ? 'red' : 'black',
													borderWidth: errors.nominal ? '2px' : '1px',
												},
												'&:hover fieldset': {
													borderColor: errors.nominal ? 'red' : 'black',
												},
												'&.Mui-focused fieldset': {
													borderColor: errors.nominal ? 'red' : 'black',
													borderWidth: errors.nominal ? '2px' : '1px',
												},
											},
										}}
									/>
								</div>
								<div className='grid grid-cols-2 gap-2'>
									{nominalModifier.map((v) => {
										return (
											<button
												className={twMerge('btn-payment', +nominal === +v ? 'selected' : '')}
												key={v}
												onClick={() => {
													if (+nominal !== +v) {
														setNominal(v);
														setValue('nominal', +v);
													} else {
														setNominal(0);
														setValue('nominal', 0);
													}
													trigger('nominal');
												}}
												type='button'
											>
												{nfComma(v)}
											</button>
										);
									})}
								</div>
								{outlet?.bank_info && <div className='flex flex-col gap-2'>{parse(outlet.bank_info)}</div>}
								<div className='grid grid-cols-2 gap-2 mt-auto'>
									<button type='button' className='w-full btn-actions' onClick={() => navigate(-1)}>
										Cancel
									</button>
									<button type='button' className='w-full btn-actions' onClick={onPay} disabled={!nominal || +nominal < 200000}>
										{isPendingGUniqueAmount && <CircleLoader className='me-2' />}
										Pay
									</button>
								</div>
							</>
						)}
						{step === 1 && (
							<div className='flex flex-col gap-4'>
								{outlet?.bank_info && <div className='flex flex-col gap-2'>{parse(outlet.bank_info)}</div>}
								{outlet?.qris_file_url && <img src={outlet.qris_file_url} alt='qris' className='w-full m-auto max-w-60' />}
								<div>
									Amount to pay: <span className='font-bold text-huge'>IDR {nfComma(nominal)}</span>
								</div>
								{/* <div>
									<button
										className='flex items-center w-full gap-2 btn-actions'
										type='button'
										onClick={() => copyToClipboard(outlet.account_number)}
									>
										<CopyAll />
										<span>Copy Account no</span>
									</button>
								</div> */}
								<div {...getRootProps({ className: 'dropzone flex flex-col gap-2 cursor-pointer' })}>
									<div className='flex flex-col items-center justify-center w-full gap-2'>
										<label className='flex flex-col items-center w-full'>
											<input {...getInputProps()} />
											<button className='flex items-center w-full gap-2 btn-actions' type='button'>
												{!isPending ? <Paid /> : <CircleLoader />}
												<span>I Already Paid</span>
											</button>
										</label>
										{file && <span className='ml-2'>{file.name}</span>}
									</div>
								</div>
							</div>
						)}
					</form>
				</div>
				<WarningTopUp
					open={isWarning}
					onClose={() => {
						setWarning(false);
					}}
					onAction={() => {
						setWarning(false);
						navigate(-1);
					}}
				/>
			</div>
			<Navigation />
		</div>
	);
}
